import React, { useState, useEffect } from 'react';
import { Quote, User } from 'lucide-react';
import api from '../../utils/axios';
import getFullUrl from '../../utils/formatUrl';

const StudentVoiceWall = () => {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchPosts = async () => {
            try {
                const res = await api.get('/student-posts/approved');
                setPosts(res.data.data || []);
            } catch (err) {
                console.error("Failed to load student voice wall", err);
            } finally {
                setLoading(false);
            }
        };
        fetchPosts();
    }, []);

    if (loading || posts.length === 0) return null;

    // Double the posts for seamless marquee loop
    const displayPosts = [...posts, ...posts];

    return (
        <section className="py-24 bg-slate-100 overflow-hidden relative border-t border-slate-200">
            {/* Background elements */}
            <div className="absolute top-0 right-0 p-10 opacity-5 pointer-events-none">
                <Quote size={200} className="text-[#003366]" />
            </div>
            
            <div className="max-w-7xl mx-auto px-4 relative z-10 mb-16">
                <div className="text-center">
                    <h3 className="text-[#cc0000] font-black uppercase tracking-[5px] text-[11px] mb-4">Student Community</h3>
                    <h2 className="text-4xl md:text-5xl font-black uppercase tracking-tighter text-slate-900 leading-tight">
                        Student <span className="text-[#cc0000]">Voice Wall</span>
                    </h2>
                    <p className="mt-6 text-slate-400 font-bold uppercase text-[10px] tracking-[4px]">Verified Success Stories from our Campus</p>
                </div>
            </div>

            <div className="relative group w-full overflow-hidden">
                <div 
                    className="flex space-x-8 whitespace-nowrap animate-marquee group-hover:pause-marquee py-10"
                    style={{
                        animation: 'marquee-wall 60s linear infinite',
                        width: 'fit-content'
                    }}
                >
                    {displayPosts.map((post, idx) => (
                        <div key={idx} className="w-[125px] md:w-[380px] bg-white border border-slate-200 p-3 md:p-10 rounded-sm shadow-xl hover:shadow-2xl transition-all duration-700 relative flex flex-col shrink-0 hover:-translate-y-2 mx-1 md:mx-0">
                            <div className="absolute top-2 right-2 md:top-4 md:right-6 opacity-10">
                                <Quote size={20} className="md:w-[50px] md:h-[50px] text-slate-900" />
                            </div>
                            
                            <div className="flex flex-col md:flex-row items-center md:items-start gap-2 md:gap-6 mb-4 md:mb-8">
                                <div className="w-10 h-10 md:w-16 md:h-16 rounded-full overflow-hidden border-2 md:border-4 border-slate-50 shadow-lg shrink-0 bg-slate-100 flex items-center justify-center">
                                    {post.photo ? (
                                        <img src={getFullUrl(post.photo)} alt={post.name} className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-500" />
                                    ) : (
                                        <User size={20} className="md:w-[30px] md:h-[30px] text-slate-400" />
                                    )}
                                </div>
                                <div className="min-w-0 text-center md:text-left">
                                    <h4 className="font-black text-[#003366] text-[10px] md:text-base uppercase tracking-tighter truncate leading-none mb-1 md:mb-2">{post.name}</h4>
                                    <div className="text-[7px] md:text-[9px] font-black text-[#cc0000] uppercase tracking-widest truncate flex items-center justify-center md:justify-start">
                                        <span className="w-1 h-1 md:w-1.5 md:h-1.5 bg-[#cc0000] rounded-full mr-1 md:mr-2"></span>
                                        {post.college}
                                    </div>
                                    <div className="hidden md:block text-[8px] font-bold text-slate-400 uppercase tracking-widest mt-1">
                                        {post.course} • Batch {post.year}
                                    </div>
                                </div>
                            </div>

                            <div className="relative">
                                <p className="text-slate-600 font-medium text-[8px] md:text-xs leading-tight md:leading-relaxed italic mb-4 md:mb-8 line-clamp-3 md:line-clamp-4 whitespace-normal border-l-2 md:border-l-4 border-[#cc0000]/10 pl-2 md:pl-4 md:pl-6 py-1 md:py-2 group-hover:border-[#cc0000] transition-colors duration-500">
                                    "{post.feedback}"
                                </p>
                            </div>

                            <div className="mt-auto pt-3 md:pt-6 border-t border-slate-50 flex items-center justify-between">
                                <div className="space-y-0.5 md:space-y-1">
                                    <span className="block text-[6px] md:text-[8px] font-black uppercase text-slate-400 tracking-widest">Achieved at</span>
                                    <span className="block text-[8px] md:text-[11px] font-black text-slate-900 uppercase tracking-tighter truncate max-w-[60px] md:max-w-none">{post.company || 'Professional Career'}</span>
                                </div>
                                <div className="w-6 h-6 md:w-10 md:h-10 rounded-full bg-slate-50 flex items-center justify-center group-hover:bg-[#cc0000]/10 transition-colors">
                                    <Quote size={10} className="md:w-[14px] md:h-[14px] text-[#cc0000]" />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <style dangerouslySetInnerHTML={{ __html: `
                @keyframes marquee-wall {
                    0% { transform: translateX(0); }
                    100% { transform: translateX(-50%); }
                }
                .pause-marquee {
                    animation-play-state: paused !important;
                }
            `}} />
        </section>
    );
};

export default StudentVoiceWall;
